import { useEventCallback } from '@mui/material';
import { Formik, useFormikContext } from 'formik';
import { PracticeEditResidence } from 'practice/view-models/practice-edit-residence';
import { PmiButton } from 'shell/components/shared/button';
import { Fragment, memo, ReactElement, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import AccordionResidence from './accordion-residence';
import { PracticeAddress } from 'shell/models/practice-address';
import { DateTime } from 'luxon';
import { Message } from 'shell/components/shared/search-messages';
import { ResidenceID } from 'practice/constants/id-practice-edit-residence';
import { transformFamilyTable } from 'practice/utils/transform-family-table';
import ModalWarning from 'shell/components/shared/modal-warning';
import validationSchema from './validation-schema';

interface StepResidenceProps {
  praticaKey?: {
    cdEnte: string;
    tipoPratica: string;
    idAnnoPratica: number;
    pgPratica: number;
  } | null;
  onAvanza?: () => void;
  onIndietro?: () => void;
}

const StepResidenceComponent = ({
  praticaKey,
  onAvanza,
  onIndietro,
}: StepResidenceProps): ReactElement => {
  const [warning, setWarning] = useState<{
    view: boolean;
    warning: Array<Message>;
    values: PracticeEditResidence;
  }>({
    view: false,
    warning: [],
    values: {},
  });

  const [indirizzo] = useHttpFetch<HttpResponse<PracticeAddress>, PracticeAddress>(
    `${__PRACTICE_API_URL__}/pratiche/indirizzi/italiano-o-estero/${praticaKey?.cdEnte}-${praticaKey?.idAnnoPratica}-IAIRE-${praticaKey?.pgPratica}`,
    {
      onBootstrap: !!praticaKey,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response) => {
        return response.data.payload;
      },
    }
  );

  const updateData = useHttpClient<HttpResponse<PracticeAddress>>(
    `${__PRACTICE_API_URL__}/pratiche/registrazione-nuovo-indirizzo`,
    {
      options: { method: 'POST' },
    }
  );

  const onSave = useEventCallback((values: PracticeEditResidence) => {
    let idAnprFamigliaConvivenza;

    if (values.typeResidence === ResidenceID.family) {
      idAnprFamigliaConvivenza = values.tableFamiliar?.item?.idAnpr;
    }

    if (values.typeResidence === ResidenceID.domesticPartnership) {
      idAnprFamigliaConvivenza = (values.tableDomesticPartnership?.body || [])[0]?.idAnpr;
    }

    let idDbLocaleFamigliaConvivenza;

    if (values.typeResidence === ResidenceID.family) {
      idDbLocaleFamigliaConvivenza = values?.tableNewFamilySubjects?.idFamigliaConvivenza;
    }

    if (values.typeResidence === ResidenceID.domesticPartnership) {
      idDbLocaleFamigliaConvivenza = (values.tableDomesticPartnership?.body || [])[0]
        ?.IdCardMunicipality;
    }

    const data = {
      pratica: {
        key: {
          codiceEnte: praticaKey?.cdEnte,
          tipoRegistro: 'IAIRE',
          idAnnoPratica: praticaKey?.idAnnoPratica,
          progressivoPratica: praticaKey?.pgPratica,
        },
      },
      casoPratica: values.typeResidence ? values.typeResidence : ResidenceID.new,
      dataDecorrenza: values.effectiveDate
        ? values.effectiveDate.toFormat('yyyy-MM-dd')
        : undefined,
      indirizzo:
        values.residence?.italian && !values.residence?.checkedResidence
          ? {
              codVia:
                values.residence?.italian?.address?.code ||
                values.residence?.italian?.addressAuto?.code ||
                undefined,
              citta: values.residence?.italian.municipality?.description || undefined,
              provincia: values.residence?.italian.municipality?.province || undefined,
              cap: values.residence?.italian?.cap || undefined,
              nomeVia:
                values.residence?.italian?.address?.description ||
                values.residence?.italian?.addressAuto?.description ||
                undefined,
              numeroCivico: values.residence?.italian?.number || undefined,
              esponente: values.residence?.italian?.exponent || undefined,
              lettera: values.residence?.italian?.letter || undefined,
              sezione: values.residence?.italian?.section || undefined,
              foglio: values.residence?.italian?.sheet || undefined,
              particellaMappale: values.residence?.italian?.particle || undefined,
              subalterno: values.residence?.italian?.sub || undefined,
              categoriaCatastale: values.residence?.italian?.cat || undefined,
              flagIgnotaDimora: values.residence?.italian?.isHomeless ? '1' : '0',
              descrizioneSpecieToponimo:
                values.residence?.italian?.descrizioneSpecieToponimo || undefined,
              codTipoVia: values.residence?.italian?.codTipoVia || undefined,
              tipoOccupazione: values?.residence?.italian?.houseOccupationType || undefined,
              sedeAgenziaEntrate: values?.residence?.italian?.officeContract || undefined,
              dataRegistrazioneAgenziaEntrate:
                values?.residence?.italian?.dateContract?.toFormat('yyyy-MM-dd') || undefined,
              numeroRegistrazioneAgenziaEntrate:
                values?.residence?.italian?.numberContract || undefined,
              notaOperatore: values?.residence?.italian?.note || undefined,
              flagAttualeResidenza:
                (values?.typeResidence === ResidenceID.new ||
                  values?.typeResidence === ResidenceID.allFamily) &&
                values.residence.flagAttualeResidenza
                  ? '1'
                  : '0',
            }
          : undefined,
      indirizzoEstero:
        values.residence?.foreign && values.residence?.checkedResidence
          ? {
              statoEstero: values.residence?.foreign.foreignState?.province || undefined,
              codComune:
                values?.typeResidence === ResidenceID.new &&
                values.residence?.foreign.municipality?.id
                  ? Number.parseInt(values.residence?.foreign.municipality?.id, 10)
                  : undefined,
              comune: values.residence?.foreign?.municipality?.description || undefined,
              codNazione:
                values?.typeResidence === ResidenceID.family &&
                values.residence?.foreign?.nationCode
                  ? Number(values.residence?.foreign?.nationCode)
                  : (values?.typeResidence === ResidenceID.new ||
                      values?.typeResidence === ResidenceID.allFamily) &&
                    values.residence?.foreign.foreignState?.id
                  ? values.residence?.foreign.foreignState?.id
                  : undefined,
              contea: values.residence?.foreign?.municipality?.province || undefined,
              cap: values.residence?.foreign?.cap || undefined,
              indirizzo: values.residence?.foreign?.address || undefined,
              civico: values.residence?.foreign?.civicNumber || undefined,
              codiceConsolato: values.residence?.foreign?.consulateCode?.id || undefined,
              codiceTerritorio:
                values.residence?.foreign?.territory?.id ||
                values.residence?.foreign.foreignState?.id ||
                undefined,
              dataIscrizioneAireConsolato: values.dateSubscriptionNumber
                ? values.dateSubscriptionNumber?.toFormat('yyyy-MM-dd')
                : undefined,
              nrIscrizioneAireConsolato: values.subscriptionNumber
                ? Number.parseInt(values.subscriptionNumber, 10)
                : undefined,
            }
          : undefined,
      // casi famiglia e convivenza
      idAnprFamigliaConvivenza: idAnprFamigliaConvivenza || undefined,
      // casi famiglia e convivenza
      idDbLocaleFamigliaConvivenza: idDbLocaleFamigliaConvivenza || undefined,
      // caso convivenza
      nomeConvivenza:
        values.typeResidence === ResidenceID.domesticPartnership
          ? values.residence?.nomeConvivenza
          : undefined,
      // caso famiglia
      personeNuovaFamiglia:
        values.typeResidence === ResidenceID.family
          ? values.tableNewFamilySubjects?.body
              ?.filter((element) => element.hideRelationship)
              ?.map((item) => ({
                generalita: item.subject3002?.generalita,
                legameSoggetto: item.subject3002?.legameSoggetto
                  ? {
                      dataDecorrenza: item.subject3002?.legameSoggetto.dataDecorrenza || undefined,
                      codiceLegame: item.subject3002?.legameSoggetto.codiceLegame || undefined,
                      descrizioneCodiceLegame:
                        item.subject3002?.legameSoggetto.descrizioneCodiceLegame || undefined,
                      progressivoComponente:
                        item.subject3002?.legameSoggetto.progressivoComponente || undefined,
                      tipoLegame: item.subject3002?.legameSoggetto.tipoLegame || undefined,
                    }
                  : undefined,
                autoveicoli: item.subject3002?.autoveicoli,
              }))
          : undefined,
    };

    (updateData({ data }) as ResponsePromise<HttpResponse<PracticeAddress>>)
      .then((response) => {
        const castedResponse = response?.data.payload;

        if ((castedResponse?.warning || []).length > 0) {
          const warningResult = (castedResponse?.warning || []).map((item, index) => {
            return {
              id: index,
              value: item || '',
            };
          });

          setWarning({ view: true, warning: warningResult, values });

          return;
        }

        toast.success('Operazione eseguita con successo');

        if (onAvanza) {
          onAvanza();
        }
      })
      .catch(() => {
        toast.error('Errore durante il salvataggio della residenza');
      });
  });

  const initialValues: PracticeEditResidence = useMemo(() => {
    if (indirizzo.isLoading || indirizzo.hasErrors || indirizzo.pristine || !indirizzo.data) {
      return {
        isFromData: false,
      };
    }

    const data: PracticeAddress = (indirizzo.data as PracticeAddress) || {};

    const dataItalian = data?.indirizzoVO || undefined;

    const dataForeign = data?.indirizzoEsteroVO || undefined;

    const convivenza = data.convivenza || undefined;

    const personeTrasferite = data?.personeTrasferite || [];

    return {
      citizenshipNumber: data.cittadinanza,
      effectiveDate: data.dataDecorrenza ? DateTime.fromISO(data.dataDecorrenza || '') : undefined,
      typeResidence: data.casoPratica?.toString() || undefined,
      dateSubscriptionNumber: data.indirizzoEsteroVO?.dataProtocolloProvenienza
        ? DateTime.fromISO(data.indirizzoEsteroVO?.dataProtocolloProvenienza)
        : null,
      subscriptionNumber: data.indirizzoEsteroVO?.progressivoEnteProvenienza || '',
      residence: {
        checkedResidence: !!dataForeign,
        nomeConvivenza: convivenza?.nomeConvivenza || undefined,
        italian: dataItalian
          ? {
              municipality: {
                description: dataItalian?.citta ? dataItalian?.citta : '',
                province: dataItalian?.provincia ? dataItalian?.provincia : '',
                provinceCode: dataItalian?.provincia ? dataItalian?.provincia : '',
              },
              cap: dataItalian?.cap || '',
              address: dataItalian?.nomeVia
                ? {
                    code: dataItalian.codVia,
                    description: dataItalian.nomeVia,
                  }
                : null,
              number: dataItalian.numeroCivico || '',
              exponent: dataItalian.esponente || '',
              section: dataItalian.sezione || '',
              sheet: dataItalian.foglio || '',
              particle: dataItalian.particellaMappale || '',
              sub: dataItalian.subalterno || '',
              cat: dataItalian.categoriaCatastale || '',
              isHomeless: dataItalian.flagIgnotaDimora === '1',
              codTipoVia: dataItalian.codTipoVia || '',
              addressAuto: dataItalian.codVia
                ? {
                    code: dataItalian.codVia,
                    description: dataItalian.nomeVia,
                  }
                : undefined,
              descrizioneSpecieToponimo: dataItalian.descrizioneSpecieToponimo || '',
              houseOccupationType: dataItalian.tipoOccupazione || '',
              officeContract: dataItalian.sedeAgenziaEntrate || '',
              dateContract: dataItalian.dataRegistrazioneAgenziaEntrate
                ? DateTime.fromISO(dataItalian.dataRegistrazioneAgenziaEntrate)
                : null,
              numberContract: dataItalian.numeroRegistrazioneAgenziaEntrate?.toString() || '',
              note: dataItalian.notaOperatore || '',
            }
          : undefined,
        foreign: dataForeign
          ? {
              foreignState: {
                id: dataForeign.codNazione,
                value: dataForeign.statoEstero,
              },
              municipality: {
                description: dataForeign.comune || '',
                province: dataForeign.contea || '',
                id: dataForeign.codComune || '',
              },
              nationCode: dataForeign?.codNazione || '',
              cap: dataForeign.cap || '',
              address: dataForeign.indirizzo || '',
              civicNumber: dataForeign.civico || '',
              consulateCode: dataForeign.codiceConsolato ? { id: dataForeign.codiceConsolato } : '',
              territory: dataForeign.codiceTerritorio
                ? { id: dataForeign.codiceTerritorio }
                : undefined,
            }
          : undefined,
      },
      tableDomesticPartnership:
        convivenza && data.casoPratica?.toString() === ResidenceID.domesticPartnership
          ? {
              body: [
                {
                  IdCardMunicipality: convivenza.idDbLocaleConvivenza || '',
                  // codeIstat mancante
                  idAnpr: convivenza.idAnprConvivenza,
                  nameDomesticPartnership: convivenza.nomeConvivenza || '',
                  address: `${dataItalian?.nomeVia || ''}, ${dataItalian?.numeroCivico || ''} ${
                    dataItalian?.esponente || dataItalian?.lettera
                      ? `/${dataItalian?.esponente || ''}${dataItalian?.lettera || ''}`
                      : ''
                  }`,
                  isActive: true,
                },
              ],
            }
          : undefined,
      tableNewFamilySubjects:
        personeTrasferite.length > 0 && data.casoPratica?.toString() === ResidenceID.family
          ? {
              body: (transformFamilyTable(personeTrasferite) || [])?.map((item) => ({
                ...item,
                relationshipValue: item.relationship || undefined,
              })),
              residence: dataItalian
                ? {
                    municipality: {
                      // id:
                      description: dataItalian?.citta || '',
                      province: dataItalian?.provincia || '',
                    },
                    cap: dataItalian?.cap || '',
                    address: dataItalian?.nomeVia
                      ? {
                          code: dataItalian.codVia,
                          description: dataItalian.nomeVia,
                        }
                      : null,
                    number: dataItalian.numeroCivico || '',
                    exponent: dataItalian.esponente || '',
                    // letter: dataItalian.lettera || '',
                    section: dataItalian.sezione || '',
                    sheet: dataItalian.foglio || '',
                    particle: dataItalian.particellaMappale || '',
                    sub: dataItalian.subalterno || '',
                    cat: dataItalian.categoriaCatastale || '',
                    isHomeless: dataItalian.flagIgnotaDimora === '1',
                    codTipoVia: dataItalian.codTipoVia || '',
                    addressAuto: dataItalian.codVia
                      ? {
                          code: dataItalian.codVia,
                          description: dataItalian.nomeVia,
                        }
                      : undefined,
                    descrizioneSpecieToponimo: dataItalian.descrizioneSpecieToponimo || '',
                    houseOccupationType: dataItalian.tipoOccupazione || '',
                    officeContract: dataItalian.sedeAgenziaEntrate || '',
                    dateContract: dataItalian.dataRegistrazioneAgenziaEntrate
                      ? DateTime.fromISO(dataItalian.dataRegistrazioneAgenziaEntrate)
                      : null,
                    numberContract: dataItalian.numeroRegistrazioneAgenziaEntrate?.toString() || '',
                    note: dataItalian.notaOperatore || '',
                  }
                : dataForeign
                ? {
                    foreignState: {
                      id: dataForeign.statoEstero,
                      value: dataForeign.statoEstero,
                    },
                    municipality: {
                      description: dataForeign.comune || '',
                      province: dataForeign.contea || '',
                      id: dataForeign.codComune || '',
                    },
                    nationCode: dataForeign?.codNazione || '',
                    cap: dataForeign.cap || '',
                    address: dataForeign.indirizzo || '',
                    civicNumber: dataForeign.civico || '',
                    consulateCode: dataForeign.codiceConsolato
                      ? { id: dataForeign.codiceConsolato }
                      : '',
                    territory: dataForeign.codiceTerritorio
                      ? { id: dataForeign.codiceTerritorio }
                      : undefined,
                  }
                : undefined,
            }
          : undefined,
      tableFamiliar:
        personeTrasferite.length > 0 && data.casoPratica?.toString() === ResidenceID.family
          ? {
              body: (transformFamilyTable(personeTrasferite) || [])?.map((item) => ({
                ...item,
                relationshipValue: item.relationship || undefined,
              })),
              item: (transformFamilyTable(personeTrasferite) || [])[0],
            }
          : undefined,
    } as PracticeEditResidence;
  }, [indirizzo]);

  const onCloseWarning = useEventCallback(() => {
    setWarning({ view: false, warning: [], values: {} });

    if (onAvanza) {
      onAvanza();
    }
  });

  const isLoading = indirizzo.isLoading || indirizzo.hasErrors || indirizzo.pristine;

  const ButtonsSectionComponent = (): ReactElement => {
    const formContext = useFormikContext<PracticeEditResidence>();

    const handleAvanti = useEventCallback(() => {
      formContext.handleSubmit();
    });

    const handleIndietro = useEventCallback(() => {
      if (onIndietro) {
        onIndietro();
      }
    });

    return (
      <div className="pmi-pt-3 pmi-d-flex pmi-justify-content-center">
        <div className="pmi-w-25 pmi-w-lg-15">
          <PmiButton variant="outlined" fullWidth color="cdmBrand" onClick={handleIndietro}>
            Indietro
          </PmiButton>
        </div>
        <div className="pmi-pl-2 pmi-w-25 pmi-w-lg-15">
          <PmiButton
            variant="outlined"
            fullWidth
            color="cdmBrandThird"
            type="button"
            disabled={!formContext.isValid}
            onClick={handleAvanti}
          >
            Avanti
          </PmiButton>
        </div>
      </div>
    );
  };

  ButtonsSectionComponent.displayName = 'Buttons section';

  return (
    <Fragment>
      {isLoading ? undefined : (
        <Formik
          initialValues={initialValues}
          onSubmit={onSave}
          validationSchema={validationSchema}
          validateOnMount={true}
        >
          <div>
            <AccordionResidence praticaKey={praticaKey} />
            <ModalWarning
              open={warning.view as boolean}
              onClose={onCloseWarning}
              message={(warning.warning as Array<Message>)?.map((el) => el.value)?.join('')}
              withAnnulment
              onAnnulment={() => setWarning({ view: false, warning: [], values: {} })}
              nameButton="Prosegui"
            />
            <ButtonsSectionComponent />
          </div>
        </Formik>
      )}
    </Fragment>
  );
};

StepResidenceComponent.displayName = 'step component Residence';

const StepResidence = memo(StepResidenceComponent);

export default StepResidence;
